import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CatalogSearch from "./search/CatalogSearch";
import CatalogSort from "../../resources/components/work-components/sort/CatalogSort";
import WorkList from "../../resources/components/work-components/work-list/WorkList";
import { useAppDispatch } from "../../../custom-hooks/useAppDispatch";
import { PlatformInfoServiceImpl } from "../../../services/PlatformInfoServiceImpl";
import { useSelector } from "react-redux";
import { selectPlatformInfo } from "../../../slices/platform-info-slice";
import ScrollToTopButton from "../../resources/components/ScrollToTopButton";
import { WorkServiceImpl } from "../../../services/WorkServiceImpl";
import { DEFAULT_PAGE, RECORDS_PER_PAGE } from "../../../domain/constants";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import {
    workSearchSortFields,
    workSearchSortSchemaFactory,
} from "../../../validation-schemas/work-schemas/WorkSearchSortSchema";
import { useUrlWorksSearchParams, useWorksSearchParams } from "../../../custom-hooks/domain/useWorksSearchParams";
import { WorkListType } from "../../../domain/models/Work";
import { getUserCoords, selectUser } from "../../../slices/user-slice";

const CatalogPage: FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [page, setPage] = useState(0);

    useEffect(() => {
        (async () => await dispatch(PlatformInfoServiceImpl.getPlatformInfoThunk()))();
    }, []);

    const platformInfo = useSelector(selectPlatformInfo);
    const user = useSelector(selectUser());

    const params = useUrlWorksSearchParams();
    const { form, setErrorKey } = useCustomFormik({
        initial: params,
        fields: workSearchSortFields,
        schema: workSearchSortSchemaFactory(t),
        onSubmit: async (_values, _actions) => {
            uploadWorks(true);
        },
    });
    const { resetUrlParams } = useWorksSearchParams(form);

    const uploadWorks = async (resetPage: boolean, resetForm = false) => {
        const currentPage = resetPage ? DEFAULT_PAGE : page + 1;
        const result = await dispatch(
            WorkServiceImpl.getWorksThunk({
                page: currentPage,
                limit: RECORDS_PER_PAGE,
                form: resetForm ? null : form.values,
                coords: await getUserCoords(user),
            })
        );
        if (result.meta.requestStatus !== "fulfilled") {
            setErrorKey("errors.common");
        }
        setPage(currentPage);
    };

    const uploadWorksCallback = useCallback(async () => {
        uploadWorks(false);
    }, [form.values, page]);

    const resetFormCallback = async () => {
        form.resetForm();
        resetUrlParams();
        uploadWorks(true, true);
    };

    return (
        <div className="relative mx-auto dmin2:w-[1280px] di6:w-[800px] ti6:w-[650px] ti7:w-[560px] tmax4:w-[360px] mb-[80px]">
            <h1 className="H48 dmax2:H24B text-textWhite mb-[60px] dmax2:mb-6">{t("catalog.title")}</h1>
            <CatalogSearch form={form} />
            <CatalogSort form={form} resetFormCallback={resetFormCallback} />
            <div className="flex justify-between items-center w-full mt-7 td4:mt-5 tmax4:mt-4 mb-[60px] dmax2:mb-10">
                <span className="H20 td4:H16 tmax4:H12 text-textFaded">
                    {t("catalog.visitorsPerDay", { visitorsCount: platformInfo.visitorsPerDay })}
                </span>
                <span className="H20 td4:H16 tmax4:H12 text-textFaded">
                    {t("catalog.ordersCreated", { ordersCount: platformInfo.worksPerDay })}
                </span>
            </div>
            <WorkList type={WorkListType.COMMON} uploadWorksCallback={uploadWorksCallback} />
            <ScrollToTopButton />
        </div>
    );
};

export default CatalogPage;
